import type { GetStaticPropsContext } from 'next';
import { useQuery } from '@sanity/react-loader';
import { groq } from 'next-sanity';

import { fetchGlossaryTermPaths } from '#/lib/sanity/fetchers/fetchGlossaryTermPaths';
import { GlossaryTermProps as GlossaryTermPropsSchema } from '#/lib/sanity/fetchers/fetchGlossaryTermProps';
import {
  checkPageSchema,
  type NextPageData,
} from '#/lib/sanity/fetchers/helpers';
import {
  loadQuery,
  loadQueryOptions,
  type QueryResponseInitial,
} from '#/lib/sanity/loader';
import {
  glossaryTerm,
  SanityGlossaryTerm,
} from '#/lib/sanity/queries/glossaryTerm';
import { site } from '#/lib/sanity/queries/site';
import { cn } from '#/lib/utils';
import { Button } from '#/components/Button';
import { Footnotes } from '#/components/Footnotes';
import { Headline } from '#/components/Headline';
import { Link } from '#/components/Link';
import { RichText } from '#/components/RichText';
import { BreadcrumbSchema } from '#/components/SeoSchema/BreadcrumbSchema';
import { GlossaryTermSchema } from '#/components/SeoSchema/GlossaryTermSchema';

export const config = {
  maxDuration: 30,
};

export async function getStaticPaths() {
  const paths = await fetchGlossaryTermPaths();

  return {
    paths,
    fallback: 'blocking',
  };
}

const glossaryTermQuery = groq`{
  'page': ${glossaryTerm},
  'site': ${site},
}`;

type GlossaryTermQueryResult = Omit<
  NextPageData<typeof SanityGlossaryTerm>,
  'draftMode'
>;

export async function getStaticProps({
  params,
  draftMode,
}: GetStaticPropsContext<{ slug: string }>) {
  const initial = await loadQuery<GlossaryTermQueryResult>(
    glossaryTermQuery,
    { slug: params!.slug },
    loadQueryOptions({ draftMode }),
  );
  const data = await checkPageSchema<typeof SanityGlossaryTerm>({
    schema: GlossaryTermPropsSchema,
    result: initial.data,
    draftMode,
    context: {
      callerName: 'glossary term getStaticProps',
      slug: params!.slug,
    },
  });
  const props = { ...data, draftMode: !!draftMode };

  if (!props.page) {
    return {
      notFound: true,
    };
  }

  return { props: { ...props, initial } };
}

export default function GlossaryTerm(
  props: NextPageData<typeof SanityGlossaryTerm> & {
    initial: QueryResponseInitial<GlossaryTermQueryResult>;
  },
) {
  const { data } = useQuery<GlossaryTermQueryResult>(
    glossaryTermQuery,
    { slug: props.page?.href.replace('/glossary/', '') ?? '' },
    { initial: props.initial },
  );
  const term = data.page ?? props.page;

  if (!term) return null;

  return (
    <>
      <div className="p-main mb-50 pt-20 m:mb-120 m:pt-50">
        <BreadcrumbSchema
          items={[
            { name: 'Glossary', path: '/glossary' },
            { name: term.title, path: term.href },
          ]}
        />
        <GlossaryTermSchema {...term} />
        <div className="mx-auto mb-50 max-w-1235 justify-between gap-x-20 m:mb-0 m:flex">
          <aside className="w-full max-w-230">
            <Button
              href="/glossary"
              variant="link"
              isBackLink
              className="mb-25 m:mb-48"
            >
              {term.backLinkLabel}
            </Button>
            <nav className="hidden space-y-15 m:block">
              {term.sidebar.map(({ _key, title, href }) => (
                <Link
                  key={_key}
                  href={href}
                  className={cn(
                    'block text-14 font-[440] leading-124 -tracking-1 transition-colors',
                    {
                      'text-grayscale-04 hover:text-charcoal':
                        href !== term.href,
                      'text-charcoal': href === term.href,
                    },
                  )}
                >
                  {title}
                </Link>
              ))}
            </nav>
          </aside>
          <div className="w-full max-w-860">
            <Headline
              value={term.title}
              variant={2}
              className="mb-25"
              as="h1"
            />
            <RichText value={term.definition} />
          </div>
        </div>
        <div className="text-center m:hidden">
          <Button href="/glossary" variant="link" isBackLink>
            {term.backLinkLabel}
          </Button>
        </div>
      </div>
      {term.footnotes ? <Footnotes {...term.footnotes} /> : null}
    </>
  );
}
